"use client";

import { useTranslation } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type PricingTableProps = {
  lng?: string;
};

const PricingTable: React.FC<PricingTableProps> = (props) => {
  const { t } = useTranslation({ ns: "pricing" });

  const BaseFeature = {
    title: t("pricing.features.base.title"),
    items: [
      {
        name: t("pricing.features.base.items.resume-count"),
        free: "2",
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.base.items.resume-import"),
        free: "3",
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.base.items.resume-template"),
        free: t("pricing.free.for.limited.time"),
        plus: t("pricing.all"),
      },
      {
        name: t("pricing.features.base.items.resume-font"),
        free: t("pricing.free.for.limited.time"),
        plus: t("pricing.all"),
      },
      {
        name: t("pricing.features.base.items.resume-export-count"),
        free: t("pricing.daily.limit", { count: 1 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.base.items.resume-hosted"),
        free: t("pricing.support"),
        plus: t("pricing.support"),
      },
    ],
  };

  const AIFeatures = {
    title: t("pricing.features.ai.title"),
    items: [
      {
        name: t("pricing.features.ai.items.optimize"),
        free: t("pricing.monthly.limit", { count: 5 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.generate"),
        free: t("pricing.monthly.limit", { count: 5 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.analyze"),
        free: t("pricing.monthly.limit", { count: 5 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.interview"),
        free: t("pricing.monthly.limit", { count: 5 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.planning"),
        free: t("pricing.monthly.limit", { count: 5 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.translation"),
        free: t("pricing.monthly.limit", { count: 1 }),
        plus: t("pricing.unlimited"),
      },
      {
        name: t("pricing.features.ai.items.sim-interview"),
        free: t("total.limit", { count: 20 }),
        plus: t("pricing.unlimited"),
      },
    ],
  };

  const Features = [BaseFeature, AIFeatures];

  return (
    <div className="relative mt-40 px-4 md:px-6">
      <div className="mb-8 flex w-full flex-col items-center justify-between border-b border-neutral-200 pb-8 md:flex-row">
        <div className="mb-6 w-full md:mb-0 md:w-[300px]">
          <h2 className="text-xl font-medium leading-normal ">
            {t("pricing.compare.title")}
          </h2>
        </div>

        <div className="w-full px-6 text-center md:w-[400px]">
          <p className=" text-2xl font-semibold ">{t("pricing.plan.free")}</p>
          <p className="mt-2 text-sm text-gray-500">
            {t("pricing.free.description")}
          </p>
        </div>

        <div className="w-full px-6 text-center md:w-[400px]">
          <p className="text-2xl font-semibold">{t("pricing.plan.plus")}</p>
          <p className="mt-2 text-sm text-gray-500">
            {t("pricing.plus.description")}
          </p>
        </div>
      </div>

      {Features.map((feature, index) => (
        <div key={feature.title} className={cn(index !== 0 && "mt-12")}>
          <p className="p-6 text-xl font-bold text-[#3D956B]">
            {feature.title}
          </p>

          <div className="w-full">
            {feature.items.map((item, i) => (
              <div
                key={item.name}
                className={cn(
                  "flex items-center justify-between p-6",
                  i % 2 === 0 && "bg-[#F2FAF1]",
                )}
              >
                <p className="w-[300px] font-medium ">{item.name}</p>

                <p className="w-[400px] text-center">{item.free}</p>

                <p className="w-[400px] text-center">{item.plus}</p>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PricingTable;
